import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle } from 'lucide-react';
import { Card, Button } from '../components';
import { authAPI } from '../services/api';

/**
 * Wachtwoord vergeten: vraagt een resetlink aan (POST /auth/forgot-password).
 * De server zegt nooit of het adres bestaat, dus wij ook niet.
 */
export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [bezig, setBezig] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);
  const [fout, setFout] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBezig(true);
    setFout('');
    try {
      await authAPI.forgotPassword(email.trim());
      setVerstuurd(true);
    } catch (err: any) {
      setFout(err.response?.data?.message || t('wachtwoordReset.fout', 'Versturen mislukt. Probeer het later opnieuw.'));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <Card>
          <div className="p-6">
            {verstuurd ? (
              <div className="text-center">
                <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">{t('wachtwoordReset.verstuurdTitel', 'Controleer je inbox')}</h1>
                <p className="text-gray-600">
                  {t('wachtwoordReset.verstuurdTekst', 'Als dit e-mailadres bij ons bekend is, ontvang je binnen enkele minuten een link om een nieuw wachtwoord in te stellen. De link is 1 uur geldig.')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-center">
                  <h1 className="text-xl font-bold text-gray-900 mb-2">{t('wachtwoordReset.vergetenTitel', 'Wachtwoord vergeten')}</h1>
                  <p className="text-sm text-gray-600">
                    {t('wachtwoordReset.vergetenUitleg', 'Vul je e-mailadres in. We sturen je een link om een nieuw wachtwoord in te stellen.')}
                  </p>
                </div>
                <div className="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    placeholder={t('wachtwoordReset.emailPlaceholder', 'jouw@email.nl')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 outline-none text-sm"
                  />
                </div>
                {fout && <p className="text-sm text-red-600">{fout}</p>}
                <Button type="submit" loading={bezig} className="w-full">
                  {t('wachtwoordReset.linkVersturen', 'Resetlink versturen')}
                </Button>
              </form>
            )}
            <p className="text-sm text-gray-500 mt-6 text-center">
              <Link to="/login" className="text-blue-600 hover:underline">{t('wachtwoordReset.terugNaarLogin', 'Terug naar inloggen')}</Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
